import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faWallet } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { getFirestore } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import { useNavigate } from 'react-router-dom';
import { loadBusinessInfoSnapshot, formatDateTime } from './businessInfoShared';

function BusinessMpesaDetails() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paybills, setPaybills] = useState([]);
  const [tills, setTills] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState(null);

  const loadPage = async () => {
    const snapshot = await loadBusinessInfoSnapshot(db);
    setPaybills(snapshot.paybills);
    setTills(snapshot.tills);
    setPaymentSettings(snapshot.paymentDetails);
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load M-Pesa details:', error);
        setPaybills([]);
        setTills([]);
        setPaymentSettings(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const refreshPage = async () => {
    try {
      setRefreshing(true);
      await loadPage();
    } catch (error) {
      console.error('Failed to refresh M-Pesa details:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const summary = useMemo(() => ({
    paybillCount: paybills.length,
    tillCount: tills.length,
    totalPaths: paybills.length + tills.length
  }), [paybills, tills]);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage businessInfoPage">
          <div className="appsHeader">
            <div>
              <p className="appsKicker">Business Info</p>
              <h4>M-Pesa Details</h4>
              <p className="appsSubtext">
                M-Pesa paybill and till instructions used for fee collection and other mobile payments.
              </p>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Paybills</span>
                <strong>{summary.paybillCount}</strong>
              </div>
              <div className="appsStatChip">
                <span>Tills</span>
                <strong>{summary.tillCount}</strong>
              </div>
              <div className="appsStatChip">
                <span>Paths</span>
                <strong>{summary.totalPaths}</strong>
              </div>
            </div>
          </div>

          <div className="appsInsightsCard">
            <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
              <div>
                <p className="appsKicker">Source</p>
                <h6 className="mb-1">
                  <FontAwesomeIcon icon={faWallet} className="me-2" />
                  Fee Payment Details
                </h6>
                <p className="appsSubtext">
                  These are the same saved collection paths the finance flow reads from settings.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <span className="cohortStatusBadge cohortStatusBadge--joined">
                  <FontAwesomeIcon icon={faWallet} className="me-1" />
                  Updated {formatDateTime(paymentSettings?.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard">
            <Button variant="outline-dark" onClick={() => navigate('/settings')}>
              Open Settings
            </Button>
            <Button variant="outline-dark" onClick={refreshPage} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          <div className="appsTableCard mb-3">
            <div className="appsTableHeader">
              <h6>Paybills</h6>
              <span>{paybills.length} saved</span>
            </div>
            <div className="table-responsive">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status" />
                  <p>Loading M-Pesa details...</p>
                </div>
              ) : paybills.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState">
                  <h6>No paybills saved yet</h6>
                  <p>Add them from Settings to display collection instructions here.</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable">
                  <thead>
                    <tr>
                      <th>Paybill Number</th>
                      <th>Account / Reference</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paybills.map((item) => (
                      <tr key={item.id}>
                        <td>{item.paybillNumber || '—'}</td>
                        <td>{item.accountNumber || '—'}</td>
                        <td>{item.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>

          <div className="appsTableCard">
            <div className="appsTableHeader">
              <h6>Tills</h6>
              <span>{tills.length} saved</span>
            </div>
            <div className="table-responsive">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status" />
                  <p>Loading M-Pesa details...</p>
                </div>
              ) : tills.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState">
                  <h6>No tills saved yet</h6>
                  <p>Add them from Settings to display till instructions here.</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable">
                  <thead>
                    <tr>
                      <th>Till Number</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tills.map((item) => (
                      <tr key={item.id}>
                        <td>{item.tillNumber || '—'}</td>
                        <td>{item.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessMpesaDetails;
